import { ImageOff } from "lucide-react";
import { type ImgHTMLAttributes, useState } from "react";
import { cn } from "../cn";

type NativeImgProps = Omit<
  ImgHTMLAttributes<HTMLImageElement>,
  "src" | "alt" | "onError" | "onLoad"
>;

export interface ImageProps extends NativeImgProps {
  /** Source URL. When absent the fallback state renders immediately. */
  readonly src: string | null | undefined;
  /** Required accessible description of the image. */
  readonly alt: string;
  /**
   * Aspect ratio for the framing box, as a CSS `aspect-ratio` value
   * (e.g. `"4 / 3"`, `"1"`). Defaults to `4 / 3` — the common photo crop.
   */
  readonly ratio?: string;
  /** How the image fills its box. Defaults to `cover`. */
  readonly fit?: "cover" | "contain";
  /** Class applied to the outer framing box. */
  readonly className?: string;
  /** Class applied to the inner `<img>` element. */
  readonly imgClassName?: string;
}

/**
 * Framed, lazy-loaded image with a shimmer placeholder while loading and a
 * graceful fallback when the source is missing or fails to load. Renders a
 * fixed-ratio box so grids stay aligned regardless of the underlying image
 * dimensions.
 *
 * This is a presentational atom: it owns loading/error state for a single
 * image only. Grouping, categorisation and zoom-to-open belong to the
 * consumers (gallery, lightbox) that compose it.
 */
export function Image({
  src,
  alt,
  ratio = "4 / 3",
  fit = "cover",
  className,
  imgClassName,
  ...imgProps
}: ImageProps) {
  const [status, setStatus] = useState<"loading" | "loaded" | "error">(
    "loading",
  );
  // Reset load state when the source changes so a reused instance (list
  // virtualization, thumbnail swapping, lightbox paging) doesn't stay stuck
  // in a stale "loaded"/"error" phase for the new image. Resetting during
  // render (rather than in an effect) avoids a one-frame flash of the
  // previous image. See: https://react.dev/learn/you-might-not-need-an-effect
  const [prevSrc, setPrevSrc] = useState(src);
  if (src !== prevSrc) {
    setPrevSrc(src);
    setStatus("loading");
  }

  const showFallback = !src || status === "error";

  return (
    <div
      style={{ aspectRatio: ratio }}
      className={cn(
        "relative w-full overflow-hidden rounded-lg bg-muted",
        className,
      )}
    >
      {showFallback ? (
        <div
          role="img"
          aria-label={alt}
          className="grid size-full place-items-center text-muted-foreground"
        >
          <ImageOff className="size-6" aria-hidden="true" />
        </div>
      ) : (
        <>
          {status === "loading" && (
            <div
              aria-hidden="true"
              className={cn(
                "absolute inset-0",
                "after:absolute after:inset-0 after:-translate-x-full after:animate-shimmer after:bg-gradient-to-r after:from-transparent after:via-foreground/5 after:to-transparent",
              )}
            />
          )}
          <img
            src={src}
            alt={alt}
            loading="lazy"
            decoding="async"
            onLoad={() => setStatus("loaded")}
            onError={() => setStatus("error")}
            className={cn(
              "size-full transition-opacity duration-300",
              fit === "cover" ? "object-cover" : "object-contain",
              status === "loaded" ? "opacity-100" : "opacity-0",
              imgClassName,
            )}
            {...imgProps}
          />
        </>
      )}
    </div>
  );
}
